import type { CSSProperties, MouseEvent, ReactNode } from 'react';
import {
  CircleAlert,
  CircleCheck,
  Info,
  LoaderCircle,
  TriangleAlert,
  X,
} from 'lucide-react';
import {
  Toaster as SonnerToaster,
  toast as sonnerToast,
  type ExternalToast,
  type ToasterProps,
} from 'sonner';

export type Id = string | number;
export type TypeOptions = 'default' | 'info' | 'success' | 'warning' | 'error';
export type Theme = 'light' | 'dark' | 'colored';
export type ToastPosition = NonNullable<ToasterProps['position']>;
export type ToastContent = ReactNode;

export interface ToastAction {
  label: ReactNode;
  onClick: (event: MouseEvent<HTMLButtonElement>) => void;
}

/**
 * Compatibility type for the subset of React Toastify options used by the app.
 * This lets existing call sites migrate to Sonner without a risky big-bang rewrite.
 */
export interface ToastOptions {
  action?: ToastAction;
  autoClose?: number | false;
  className?: string;
  closeButton?: boolean;
  closeOnClick?: boolean;
  description?: ReactNode;
  draggable?: boolean;
  hideProgressBar?: boolean;
  onClick?: (event?: MouseEvent<HTMLDivElement>) => void;
  onClose?: () => void;
  pauseOnHover?: boolean;
  position?: ToastPosition;
  progress?: number;
  style?: CSSProperties;
  theme?: Theme;
  toastId?: Id;
  type?: TypeOptions;
}

export interface ToastContainerProps extends ToastOptions {
  limit?: number;
  newestOnTop?: boolean;
}

const DEFAULT_DURATION = 3500;
let toastSequence = 0;

const iconShell = (tone: string, icon: ReactNode) => (
  <span className={`aimdoc-toast-icon ${tone}`} aria-hidden="true">
    {icon}
  </span>
);

const icons = {
  success: iconShell('aimdoc-toast-icon--success', <CircleCheck size={16} strokeWidth={2.2} />),
  error: iconShell('aimdoc-toast-icon--error', <CircleAlert size={16} strokeWidth={2.2} />),
  info: iconShell('aimdoc-toast-icon--info', <Info size={16} strokeWidth={2.2} />),
  warning: iconShell('aimdoc-toast-icon--warning', <TriangleAlert size={16} strokeWidth={2.2} />),
  loading: iconShell('aimdoc-toast-icon--loading', <LoaderCircle size={16} strokeWidth={2.2} />),
  close: <X size={14} strokeWidth={2} />,
};

const toSonnerOptions = (options: ToastOptions = {}): ExternalToast => ({
  action: options.action,
  className: options.className,
  closeButton: options.closeButton,
  description: options.description,
  dismissible: options.draggable !== false,
  duration:
    options.autoClose === false
      ? Infinity
      : typeof options.autoClose === 'number'
        ? options.autoClose
        : DEFAULT_DURATION,
  id: options.toastId,
  onAutoClose: options.onClose ? () => options.onClose?.() : undefined,
  onDismiss: options.onClose ? () => options.onClose?.() : undefined,
  position: options.position,
  style: options.style,
});

const withClickHandler = (content: ToastContent, options: ToastOptions, id?: Id) => {
  if (!options.onClick) return content;

  const activate = (event?: MouseEvent<HTMLDivElement>) => {
    options.onClick?.(event);
    if (options.closeOnClick !== false && id !== undefined) {
      sonnerToast.dismiss(id);
    }
  };

  return (
    <div
      className="aimdoc-toast-click-target"
      onClick={activate}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          activate();
        }
      }}
    >
      {content}
    </div>
  );
};

const show = (
  kind: 'message' | 'success' | 'error' | 'info' | 'warning',
  content: ToastContent,
  options: ToastOptions = {},
) => {
  const id =
    options.toastId ??
    (options.onClick ? `aimdoc-toast-${++toastSequence}` : undefined);
  const message = withClickHandler(content, options, id);
  const sonnerOptions = toSonnerOptions({ ...options, toastId: id });

  return kind === 'message'
    ? sonnerToast(message, sonnerOptions)
    : sonnerToast[kind](message, sonnerOptions);
};

export const toast = {
  success: (content: ToastContent, options?: ToastOptions) => show('success', content, options),
  error: (content: ToastContent, options?: ToastOptions) => show('error', content, options),
  info: (content: ToastContent, options?: ToastOptions) => show('info', content, options),
  warning: (content: ToastContent, options?: ToastOptions) => show('warning', content, options),
  warn: (content: ToastContent, options?: ToastOptions) => show('warning', content, options),
  dismiss: (id?: Id) => sonnerToast.dismiss(id),
};

/**
 * Render exactly once at the application root. The name is retained so the
 * existing root component can switch libraries without changing its contract.
 */
export function ToastContainer(_props: ToastContainerProps) {
  return (
    <SonnerToaster
      className="aimdoc-toaster"
      closeButton
      duration={DEFAULT_DURATION}
      gap={10}
      icons={icons}
      mobileOffset={16}
      offset={20}
      pauseWhenPageIsHidden
      position="top-right"
      richColors={false}
      visibleToasts={4}
    />
  );
}
